import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet'
import { File, ResourceAcknowledgement } from '@nextforce/api'
import { Icon } from '@rneui/base'
import { makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { GenerateFileReportParams } from 'file_folder_repository/queries/shared/types'
import {
  AcknowledgementSection,
  createSectionedList,
  getAcknowledgementSectionKey,
  getAcknowledgementSectionTitle,
  getDaysUntilDue,
  sortByAcknowledgementSection,
} from 'file_folder_repository/util'
import { FileFolderDetailsView } from 'file_folder_repository/views/file_folder_details_bottom_sheet'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, Platform, View } from 'react-native'
import Animated, {
  FadeInUp,
  FadeOutUp,
  LinearTransition,
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { RoundedButton } from 'shared/components/rounded-button'
import { UserAvatar } from 'shared/components/user/user_avatar'

interface FileAcknowledgementsViewProps {
  file: File | undefined
  acknowledgements: ResourceAcknowledgement[] | undefined
  onGenerateReport: (type: GenerateFileReportParams['type']) => void
  isGeneratingReport: boolean
  onPressThumbnail: () => void
}

export function FileAcknowledgementsView({
  file,
  acknowledgements,
  onGenerateReport,
  isGeneratingReport,
  onPressThumbnail,
}: FileAcknowledgementsViewProps) {
  const styles = useStyles()
  const bottomSheetRef = useRef<BottomSheet>(null)

  const handleOpenReportModal = useCallback(() => {
    bottomSheetRef.current?.snapToIndex(0)
  }, [])

  return (
    <View style={styles.container}>
      <FileDetailsHeader
        file={file}
        onGenerateReport={handleOpenReportModal}
        onPressThumbnail={onPressThumbnail}
      />
      <AcknowledgementList acknowledgements={acknowledgements} />
      <ReportModal
        bottomSheetRef={bottomSheetRef}
        onGenerateReport={onGenerateReport}
        isGeneratingReport={isGeneratingReport}
      />
    </View>
  )
}

interface FileDetailsHeaderProps {
  file: File | undefined
  onGenerateReport: () => void
  onPressThumbnail: () => void
}

function FileDetailsHeader({
  file,
  onGenerateReport,
  onPressThumbnail,
}: FileDetailsHeaderProps) {
  const styles = useStyles()
  return (
    <View style={styles.headerContainer}>
      <FileFolderDetailsView
        itemId={file?.id}
        itemType={file?.type}
        showActions={false}
        showPath={false}
        showOverlay
        onPressThumbnail={onPressThumbnail}
      />
      <RoundedButton
        title={t('transactionsReport')}
        onPress={onGenerateReport}
        containerStyle={styles.reportButtonContainer}
        emphasized
      />
    </View>
  )
}

interface AcknowledgementListProps {
  acknowledgements: ResourceAcknowledgement[] | undefined
}

function AcknowledgementList({ acknowledgements }: AcknowledgementListProps) {
  const styles = useStyles()

  const listItems = useMemo(() => {
    if (!acknowledgements) return []

    const sectionedAcks = [...acknowledgements]
      .map(ack => {
        const daysUntilDue = getDaysUntilDue(ack.dueDate)
        const sectionTitle = getAcknowledgementSectionTitle(
          daysUntilDue,
          ack.isAcknowledged
        )
        return { acknowledgement: ack, sectionTitle }
      })
      .sort(sortByAcknowledgementSection)

    return createSectionedList(sectionedAcks, item => ({
      type: 'acknowledgement',
      acknowledgement: item.acknowledgement,
    }))
  }, [acknowledgements])

  const renderItem = useCallback(
    ({ item }: { item: AcknowledgementSection }) => {
      if (item.type === 'header') {
        return (
          <Animated.View layout={LinearTransition}>
            <Text style={styles.sectionHeader}>{item.sectionTitle}</Text>
          </Animated.View>
        )
      }
      if (item.type === 'acknowledgement' && item.acknowledgement) {
        return <AcknowledgementItem acknowledgement={item.acknowledgement} />
      }
      return null
    },
    [styles.sectionHeader]
  )

  return (
    <View style={styles.acknowledgementListContainer}>
      <Animated.FlatList
        data={listItems}
        renderItem={renderItem}
        keyExtractor={(item, index) =>
          getAcknowledgementSectionKey(item, index)
        }
        contentContainerStyle={styles.listContainer}
        itemLayoutAnimation={LinearTransition}
      />
    </View>
  )
}

interface ReportModalProps {
  bottomSheetRef: React.RefObject<BottomSheet | null>
  onGenerateReport: (reportType: 'YTD' | 'PREV_YEAR') => void
  isGeneratingReport: boolean
}

function ReportModal({
  bottomSheetRef,
  onGenerateReport,
  isGeneratingReport,
}: ReportModalProps) {
  const styles = useStyles()
  const { theme } = useTheme()
  const { bottom } = useSafeAreaInsets()
  const [selectedReportType, setSelectedReportType] = useState<
    'YTD' | 'PREV_YEAR' | null
  >('YTD')
  const [showConfirmation, setShowConfirmation] = useState(true)
  const [wasGenerating, setWasGenerating] = useState(false)

  useEffect(() => {
    if (isGeneratingReport) {
      setWasGenerating(true)
    }
  }, [isGeneratingReport])

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
      />
    ),
    []
  )

  const handleCloseReportModal = useCallback(() => {
    bottomSheetRef.current?.close()
    // setShowConfirmation(false)
    // setSelectedReportType(null)
    setWasGenerating(false)
  }, [bottomSheetRef])

  const handleReportTypeSelect = (type: 'YTD' | 'PREV_YEAR') => {
    setSelectedReportType(type)
    setShowConfirmation(true)
  }

  const handleConfirmGenerate = () => {
    if (selectedReportType) {
      onGenerateReport(selectedReportType)
      //  setShowConfirmation(false)
    }
  }

  return (
    <BottomSheet
      ref={bottomSheetRef}
      index={-1}
      snapPoints={['60%']}
      enablePanDownToClose
      backdropComponent={renderBackdrop}
      handleIndicatorStyle={styles.bottomSheetHandleIndicator}
      backgroundStyle={styles.bottomSheetBackground}
      animateOnMount={Platform.OS !== 'ios'}
    >
      <BottomSheetScrollView contentContainerStyle={{ paddingBottom: bottom }}>
        <View style={styles.reportModalContainer}>
          {isGeneratingReport ? (
            <>
              <Text style={styles.reportModalTitle}>
                {t('generatingReport')}
              </Text>
              <ActivityIndicator
                style={styles.activityIndicator}
                size="large"
                color={theme.colors.primary}
              />
              <Text style={styles.reportModalMessage}>
                {t('reportIsBeingGenerated')}
              </Text>
              <RoundedButton
                title={t('dismiss')}
                onPress={handleCloseReportModal}
                containerStyle={styles.reportOptionButton}
              />
            </>
          ) : wasGenerating ? (
            <>
              <Text style={styles.reportModalTitle}>
                {t('reportGenerated')}
              </Text>
              <Text style={styles.reportModalMessage}>
                {t('reportGeneratedText')}
              </Text>
              <RoundedButton
                title={t('dismiss')}
                onPress={handleCloseReportModal}
                containerStyle={styles.reportOptionButton}
              />
            </>
          ) : showConfirmation ? (
            <>
              <Text style={styles.reportModalTitle}>
                {t('confirmGeneration')}
              </Text>
              <Text style={styles.reportModalMessage}>
                {t('generateReportText')}
              </Text>
              <View style={styles.reportOptionsContainer}>
                <RoundedButton
                  title={t('proceed')}
                  onPress={handleConfirmGenerate}
                  containerStyle={styles.reportOptionButton}
                  emphasized
                />
                <RoundedButton
                  title={t('cancel')}
                  onPress={() => {
                    // setShowConfirmation(false)
                    // setSelectedReportType(null)
                    handleCloseReportModal()
                  }}
                  containerStyle={styles.reportOptionButton}
                />
              </View>
            </>
          ) : (
            <>
              <Text style={styles.reportModalTitle}>
                {t('selectReportType')}
              </Text>
              <View style={styles.reportOptionsContainer}>
                <RoundedButton
                  title={t('yearToDate')}
                  onPress={() => handleReportTypeSelect('YTD')}
                  containerStyle={styles.reportOptionButton}
                />
                <RoundedButton
                  title={t('previousYear')}
                  onPress={() => handleReportTypeSelect('PREV_YEAR')}
                  containerStyle={styles.reportOptionButton}
                />
              </View>
            </>
          )}
        </View>
      </BottomSheetScrollView>
    </BottomSheet>
  )
}

interface AcknowledgementItemProps {
  acknowledgement: ResourceAcknowledgement
}

function AcknowledgementItem({ acknowledgement }: AcknowledgementItemProps) {
  const [expanded, setExpanded] = useState(false)
  const styles = useStyles()
  const { theme } = useTheme()

  const dueDate = new Date(acknowledgement.dueDate)
  const isOverdue = dueDate < new Date() && !acknowledgement.isAcknowledged

  return (
    <Animated.View
      style={styles.acknowledgementItemContainer}
      layout={LinearTransition}
    >
      <PressableOpacity onPress={() => setExpanded(!expanded)}>
        <View style={styles.acknowledgementHeader}>
          <View style={styles.userInfoContainer}>
            <UserAvatar
              user={acknowledgement.user}
              size={40}
              showAvatarIfNoPhone
            />
            <View style={styles.userTextContainer}>
              <Text style={styles.userName}>
                {`${acknowledgement.user.firstName} ${acknowledgement.user.lastName}`}
              </Text>
              <Text style={styles.dueDate}>
                {`Due: ${dueDate.toLocaleDateString()}`}
              </Text>
            </View>
          </View>
          <View style={styles.statusContainer}>
            {acknowledgement.requireSign && (
              <View style={styles.signatureContainer}>
                <Text style={styles.signatureText}>{t('sign')}</Text>
                <Icon
                  name="file-signature"
                  type="font-awesome-5"
                  solid
                  size={16}
                  color={theme.colors.grey1}
                  containerStyle={styles.signatureIconContainer}
                />
              </View>
            )}
            <Icon
              name={
                acknowledgement.isAcknowledged ? 'checkcircle' : 'clockcircle'
              }
              size={24}
              color={
                acknowledgement.isAcknowledged
                  ? theme.colors.success
                  : isOverdue
                  ? theme.colors.error
                  : theme.colors.warning
              }
              type="antdesign"
            />
            <Icon
              name={expanded ? 'chevron-up' : 'chevron-down'}
              type="material-design"
              size={24}
              color={theme.colors.grey3}
              style={styles.expandIcon}
            />
          </View>
        </View>
      </PressableOpacity>

      {expanded && (
        <Animated.View
          entering={FadeInUp}
          exiting={FadeOutUp}
          style={styles.acknowledgementDetails}
        >
          <View style={styles.requestedByContainer}>
            <Text style={styles.requestedByLabel}>{t('requestedBy')}</Text>
            <View style={styles.requestedByAvatarContainer}>
              <UserAvatar
                user={acknowledgement.requester}
                size={30}
                showAvatarIfNoPhone
              />
              <Text style={styles.requestedBy}>
                {`${acknowledgement.requester.firstName} ${acknowledgement.requester.lastName}`}
              </Text>
            </View>
          </View>
          <Text style={styles.statementLabel}>{t('statement')}</Text>
          <Text style={styles.statement}>{acknowledgement.statement}</Text>
          <Text style={styles.acknowledgedDateLabel}>{t('requestedOn')}</Text>
          <Text style={styles.acknowledgedDate}>
            {new Date(acknowledgement.created).toLocaleString()}
          </Text>
          {acknowledgement.isAcknowledged && (
            <>
              <Text style={styles.acknowledgedDateLabel}>
                {t('acknowledgedOn')}
              </Text>
              <Text style={styles.acknowledgedDate}>
                {new Date(acknowledgement.acknowledgedAt!).toLocaleString()}
              </Text>
            </>
          )}
        </Animated.View>
      )}
    </Animated.View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  headerContainer: {
    padding: 16,
  },
  reportButtonContainer: {
    marginTop: 16,
  },
  listContainer: {
    flexGrow: 1,
  },
  sectionHeader: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
    marginLeft: 16,
  },
  acknowledgementItemContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.white,
    borderRadius: 8,
    margin: 5,
    padding: 12,
  },
  acknowledgementHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  userInfoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  userTextContainer: {
    marginLeft: 12,
    flex: 1,
  },
  userName: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  dueDate: {
    fontSize: 14,
    color: theme.colors.grey3,
    marginTop: 4,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  signatureContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 0.5,
    borderColor: theme.colors.grey3,
    borderRadius: 15,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 8,
  },
  signatureText: {
    marginRight: 5,
    fontSize: 14,
  },
  expandIcon: {
    marginLeft: 8,
  },
  acknowledgementDetails: {
    marginTop: 12,
    paddingTop: 12,
  },
  statementLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  statement: {
    fontSize: 14,
    color: theme.colors.grey1,
  },
  acknowledgedDateLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 8,
    marginBottom: 4,
  },
  acknowledgedDate: {
    fontSize: 14,
    color: theme.colors.grey1,
  },
  reportModalContainer: {
    padding: 16,
  },
  reportModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  reportOptionsContainer: {
    gap: 12,
  },
  reportOptionButton: {
    marginVertical: 4,
  },
  bottomSheetBackground: {
    backgroundColor: theme.colors.background,
  },
  bottomSheetHandleIndicator: {
    backgroundColor: theme.colors.grey3,
  },
  requestedByContainer: {
    marginBottom: 10,
  },
  requestedByAvatarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  requestedByLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginRight: 8,
    marginBottom: 5,
  },
  requestedBy: {
    fontSize: 14,
    marginLeft: 8,
  },
  signatureIconContainer: { paddingVertical: 5 },
  reportModalMessage: {
    fontSize: 14,
    color: theme.colors.grey1,
    textAlign: 'center',
    marginBottom: 16,
  },
  activityIndicator: {
    marginBottom: 16,
  },
  acknowledgementListContainer: {
    flex: 1,
    marginHorizontal: 5,
  },
}))
